import React from 'react';
import {useMemo, useCallback} from 'react';
import {Popup} from 'semantic-ui-react';
import * as Panel2 from './panel';
import * as ExpressionEditor from './ExpressionEditor';
import * as CG from './cg/graph';
import * as HL from './cg/hl';
import * as Op from './cg/ops';
import * as LLReact from './cg/cgreact';
import makeComp from '../../util/profiler';
import PanelNameEditor from './editors/PanelNameEditor';
import {PanelComp2} from './PanelComp';
import {getPanelStacksForType} from './availablePanels';
import * as Types from './types';
import WBIcon from '../elements/WBIcon';
// import * as S from './SuperTable.styles';

const inputType = {type: 'array' as const, objectType: 'any' as const};

export interface FacetConfig {
  facetX: CG.Node;

  facetY: CG.Node;

  cell: CG.Node;
  cellPanelId?: string;
  cellPanelConfig?: any;

  detail: CG.Node;
  detailPanelId?: string;
  detailPanelConfig?: any;
}

export function emptyFacet(inputNode: CG.Node): FacetConfig {
  // TODO: Change these var names. x is wrong for facetX
  return {
    facetX: CG.varNode(inputNode.type, 'x'),
    facetY: CG.varNode(inputNode.type, 'x'),
    cellPanelConfig: {},
    cell: CG.varNode(inputNode.type, 'x'),
    detail: CG.varNode(inputNode.type, 'x'),
    detailPanelConfig: {},
  };
}

type PanelFacet = Panel2.PanelProps<typeof inputType, FacetConfig>;

// TODO: make into a helper function (stole from paneltable2)

const SelectPanel: React.FC<{
  inputNode: CG.Node;
  selectFunction: CG.Node;
  panelId: string | undefined;
  config: any;
  panelContext: any;
  updateConfig(newConfig: any): void;
  updatePanelContext(newContext: any): void;
}> = makeComp(
  ({
    inputNode,
    selectFunction,
    panelId,
    config,
    panelContext,
    updateConfig,
    updatePanelContext,
  }) => {
    const selectedNode = useMemo(
      () => HL.callFunction(selectFunction, {x: inputNode}),
      [selectFunction, inputNode]
    );

    const {curPanelId, handler} = useMemo(
      () => getPanelStacksForType(selectedNode.type, panelId),
      [selectedNode.type, panelId]
    );

    return (
      <div>
        {curPanelId == null ? (
          <div>No panel for type {Types.toString(selectFunction.type)}</div>
        ) : (
          <div>
            {handler != null && (
              <PanelComp2
                input={{path: selectedNode}}
                inputType={selectFunction.type}
                loading={false}
                panelSpec={handler}
                configMode={false}
                context={panelContext}
                config={config}
                updateConfig={updateConfig}
                updateContext={updatePanelContext}
              />
            )}
          </div>
        )}
      </div>
    );
  },
  {id: 'PanelFacetCell'}
);

const PanelFacet2: React.FC<PanelFacet> = props => {
  const {
    input,
    context,
    updateConfig: propsUpdateConfig,
    updateContext,
  } = props;

  const inputNode = input.path;
  const exampleInputNode = useMemo(
    () =>
      Op.opIndex({
        arr: inputNode as any,
        index: CG.varNode('number', 'n'),
      }),
    [inputNode]
  );

  const config = useMemo(
    () =>
      props.config == null || props.config.facetX == null
        ? emptyFacet(exampleInputNode)
        : props.config,
    [props.config, exampleInputNode]
  );
  // Sadly this depends on config, so will change when any config key changes
  // TODO: probably need to fix for perf
  const updateConfig = useCallback(
    (newConfig: any) => propsUpdateConfig({...config, ...newConfig}),
    [config, propsUpdateConfig]
  );

  const xNode = useMemo(
    () =>
      Op.opGroupBy({
        arr: inputNode,
        groupByFn: Op.constNodeUnsafe(config.facetX.type, config.facetX) as any,
      }),
    [inputNode, config.facetX]
  );
  const xFrame = useMemo(() => ({x: exampleInputNode}), [exampleInputNode]);
  const updateFacetX = useCallback(
    (node: CG.Node) => {
      const newGroupBy = Op.opGroupBy({
        arr: inputNode,
        groupByFn: Op.constNodeUnsafe(node.type, node) as any,
      });
      const newExampleXNode = Op.opIndex({
        arr: newGroupBy as any,
        index: CG.varNode('number', 'n'),
      });
      console.log('NEW EXAMPLE NODE TYPE', newExampleXNode);
      updateConfig({facetX: node, cell: CG.varNode(newExampleXNode.type, 'x')});
    },
    [updateConfig, inputNode]
  );

  const exampleXNode = useMemo(
    () =>
      Op.opIndex({
        arr: xNode as any,
        index: CG.varNode('number', 'n'),
      }),
    [xNode]
  );
  const cellFrame = useMemo(() => ({x: exampleXNode}), [exampleXNode]);
  const updateCell = useCallback(
    (node: CG.Node) => updateConfig({cell: node}),
    [updateConfig]
  );
  const updateDetail = useCallback(
    (node: CG.Node) => updateConfig({detail: node}),
    [updateConfig]
  );

  // TODO: Get rid of useEach by making a map? No we need to pull into react
  const xNodeUse = LLReact.useEach(xNode as any);
  const xNodes = xNodeUse.result;

  // console.log('CONFIG.CELL', config.cell);
  const cellKeyFunction = useMemo(
    () =>
      Op.opGetTag({
        obj: CG.varNode(
          Op.opIndex({arr: xNode as any, index: CG.varNode('number', 'n')})
            .type,
          'x'
        ) as any,
      }),
    [xNode]
  );
  const cellValueFunction = useMemo(
    () =>
      HL.callFunction(config.cell, {
        x: CG.varNode(
          Op.opIndex({arr: xNode as any, index: CG.varNode('number', 'n')})
            .type,
          'x'
        ) as any,
      }),
    [config.cell, xNode]
  );

  const detailValueFunction = useMemo(
    () =>
      HL.callFunction(config.detail, {
        x: CG.varNode(
          Op.opIndex({arr: xNode as any, index: CG.varNode('number', 'n')})
            .type,
          'x'
        ) as any,
      }),
    [config.detail, xNode]
  );

  const exampleCellNode = useMemo(
    () => HL.callFunction(config.cell, {x: exampleXNode}),
    [config.cell, exampleXNode]
  );
  const exampleDetailNode = useMemo(
    () => HL.callFunction(config.detail, {x: exampleXNode}),
    [config.detail, exampleXNode]
  );

  const {
    curPanelId: cellPanelId,
    stackIds,
    handler: cellHandler,
  } = getPanelStacksForType(exampleCellNode.type, config.cellPanelId);
  const updateCellPanelId = useCallback(
    (newCellPanelId: string) => updateConfig({cellPanelId: newCellPanelId}),
    [updateConfig]
  );
  const updateCellPanelConfig = useCallback(
    (newCellPanelConfig: any) =>
      updateConfig({cellPanelConfig: newCellPanelConfig}),
    [updateConfig]
  );

  const {
    curPanelId: detailPanelId,
    stackIds: detailStackIds,
    handler: detailHandler,
  } = getPanelStacksForType(exampleDetailNode.type, config.cellPanelId);
  const updateDetailPanelId = useCallback(
    (newPanelId: string) => updateConfig({detailPanelId: newPanelId}),
    [updateConfig]
  );
  const updateDetailPanelConfig = useCallback(
    (newPanelConfig: any) => updateConfig({detailPanelConfig: newPanelConfig}),
    [updateConfig]
  );

  if (xNodeUse.loading) {
    return <div></div>;
  }
  return (
    <div>
      <div>
        facetX:{' '}
        <ExpressionEditor.ExpressionEditor
          frame={xFrame}
          node={config.facetX}
          updateNode={updateFacetX}
        />
      </div>
      <div style={{display: 'flex', alignItems: 'center'}}>
        cell:{' '}
        {config.cell.nodeType !== 'var' && (
          <>
            <PanelNameEditor
              autocompleteOptions={stackIds.map(si => ({
                name: si.displayName,
                value: si.id,
              }))}
              value={cellPanelId ?? ''}
              setValue={val => updateCellPanelId(val as string)}
            />
            (
          </>
        )}
        <ExpressionEditor.ExpressionEditor
          frame={cellFrame}
          node={config.cell}
          updateNode={updateCell}
        />
        {config.cell.nodeType !== 'var' && ')'}
        {config.cell.nodeType !== 'var' && (
          <Popup
            basic
            className="wb-table-action-popup"
            on="click"
            position="bottom left"
            trigger={
              <WBIcon
                className="column-actions-trigger"
                style={{cursor: 'pointer', marginLeft: 4, marginRight: 0}}
                name="configuration"
              />
            }
            content={
              cellHandler != null ? (
                <PanelComp2
                  input={{path: exampleCellNode}}
                  inputType={cellValueFunction.type}
                  loading={false}
                  panelSpec={cellHandler}
                  configMode={true}
                  context={context}
                  config={config.cellPanelConfig}
                  updateConfig={updateCellPanelConfig}
                  updateContext={updateContext}
                />
              ) : (
                undefined
              )
            }
          />
        )}
      </div>
      <div>
        detail:{' '}
        {config.detail.nodeType !== 'var' && (
          <>
            <PanelNameEditor
              autocompleteOptions={detailStackIds.map(si => ({
                name: si.displayName,
                value: si.id,
              }))}
              value={detailPanelId ?? ''}
              setValue={val => updateDetailPanelId(val as string)}
            />
            (
          </>
        )}
        <ExpressionEditor.ExpressionEditor
          frame={cellFrame}
          node={config.detail}
          updateNode={updateDetail}
        />
        {config.detail.nodeType !== 'var' && ')'}
        {config.detail.nodeType !== 'var' && (
          <Popup
            basic
            className="wb-table-action-popup"
            on="click"
            position="bottom left"
            trigger={
              <WBIcon
                className="column-actions-trigger"
                style={{cursor: 'pointer', marginLeft: 4, marginRight: 0}}
                name="configuration"
              />
            }
            content={
              detailHandler != null ? (
                <PanelComp2
                  input={{path: exampleDetailNode}}
                  inputType={detailValueFunction.type}
                  loading={false}
                  panelSpec={detailHandler}
                  configMode={true}
                  context={context}
                  config={config.detailPanelConfig}
                  updateConfig={updateDetailPanelConfig}
                  updateContext={updateContext}
                />
              ) : (
                undefined
              )
            }
          />
        )}
      </div>
      <div style={{display: 'flex', flexWrap: 'wrap'}}>
        {xNodes.map((cellNode, i) =>
          cellHandler == null ? (
            <div key={i}>no handler</div>
          ) : (
            <Popup
              position="bottom left"
              hoverable
              trigger={
                <div style={{marginRight: 16}}>
                  <SelectPanel
                    inputNode={cellNode}
                    selectFunction={cellKeyFunction}
                    panelId={undefined}
                    config={config.cellPanelConfig}
                    panelContext={context}
                    updateConfig={updateCellPanelConfig}
                    updatePanelContext={updateContext}
                  />
                  <SelectPanel
                    inputNode={cellNode}
                    selectFunction={cellValueFunction}
                    panelId={config.cellPanelId}
                    config={config.cellPanelConfig}
                    panelContext={context}
                    updateConfig={updateCellPanelConfig}
                    updatePanelContext={updateContext}
                  />
                </div>
              }
              content={
                <SelectPanel
                  inputNode={cellNode}
                  selectFunction={detailValueFunction}
                  panelId={config.detailPanelId}
                  config={config.detailPanelConfig}
                  panelContext={context}
                  updateConfig={updateDetailPanelConfig}
                  updatePanelContext={updateContext}
                />
              }
            />
          )
        )}
      </div>
    </div>
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'facet2',
  Component: PanelFacet2,
  inputType,
};
